import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/layout/Header";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Check } from "lucide-react";

interface GalleryImage {
  original_image_url: string;
  staged_image_url: string;
}

// Add your specific image URLs here
const GALLERY_IMAGES: GalleryImage[] = [
  {
    original_image_url: "YOUR_ORIGINAL_IMAGE_URL_1",
    staged_image_url: "YOUR_STAGED_IMAGE_URL_1"
  },
  {
    original_image_url: "YOUR_ORIGINAL_IMAGE_URL_2",
    staged_image_url: "YOUR_STAGED_IMAGE_URL_2"
  },
  {
    original_image_url: "YOUR_ORIGINAL_IMAGE_URL_3",
    staged_image_url: "YOUR_STAGED_IMAGE_URL_3"
  }
];

// Přidej své obrázky do složky public/images a zde je pojmenuj
const BEFORE_AFTER_IMAGES = [
  {
    before: '/images/before1.jpg',
    after: '/images/after1.jpg',
    popis: 'Obývací pokoj'
  },
  {
    before: '/images/before2.jpg',
    after: '/images/after2.jpg',
    popis: 'Ložnice'
  },
  {
    before: '/images/before3.jpg',
    after: '/images/after3.jpg',
    popis: 'Kuchyně'
  },
  {
    before: '/images/before4.jpg',
    after: '/images/after4.jpg',
    popis: 'Pracovna'
  },
];

const Index = () => {
  const { user } = useAuth();
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        const { data: projects, error } = await supabase
          .from('projects')
          .select('original_image_url, staged_image_url')
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(6);

        if (error) {
          console.error('Error fetching gallery images:', error);
          return;
        }

        setGalleryImages(projects || []);
      } catch (error) {
        console.error('Error in gallery fetch:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryImages();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-gray-50 to-white py-20">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              {/* Logo a název odstraním, vracím nadpis a fotky */}
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Vytvořte si vysněný interiér snadno a rychle pomocí AI
              </h1>
              {/* Dvojice obrázků pod nadpisem */}
              <div className="flex flex-col md:flex-row justify-center items-center gap-6 mb-10">
                <img src="/images/hero1.jpg" alt="Inspirace 1" className="rounded-lg max-h-[36rem] w-auto" style={{ maxWidth: '100%', height: 'auto' }} />
                <img src="/images/hero2.jpg" alt="Inspirace 2" className="rounded-lg max-h-[36rem] w-auto" style={{ maxWidth: '100%', height: 'auto' }} />
              </div>
              <p className="text-xl text-gray-600 mb-6 max-w-2xl mx-auto">
                Nahrajte fotografii interiéru, zvolte si styl a nechte umělou inteligenci vykouzlit realisticky zařízenou místnost.
              </p>
              {!user && (
                <div className="bg-[#14A48D] text-white p-4 rounded-lg mb-8 max-w-md mx-auto">
                  <p className="font-semibold">🎁 Při registraci získáte 3 kredity zdarma!</p>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                {user ? (
                  <Link to="/new-project">
                    <Button size="lg" className="bg-[#14A48D] hover:bg-[#11997C] text-white px-8 font-bold">
                      Vytvořit nový projekt
                    </Button>
                  </Link>
                ) : (
                  <Link to="/register">
                    <Button size="lg" className="bg-[#14A48D] hover:bg-[#11997C] text-white px-8">
                      Zkus zadarmo
                    </Button>
                  </Link>
                )}
              </div>

              <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Check className="text-emerald-600 w-5 h-5" />
                  <span className="font-semibold">Fotorealistické výsledky</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Check className="text-emerald-600 w-5 h-5" />
                  <span className="font-semibold">Do 15 minut hotovo</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Check className="text-emerald-600 w-5 h-5" />
                  <span className="font-semibold">Odstranění nábytku v ceně</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Check className="text-emerald-600 w-5 h-5" />
                  <span className="font-semibold">Úpravy na pár kliků</span>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Credit System */}
        <section className="py-20 bg-gray-50">
          <div className="container px-4 mx-auto">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-6">Jen za 25,- Kč <span className="text-base">(při koupi professinal plánu)</span></h2>
              <p className="text-xl text-gray-600 mb-10">
                Naši AI jsme ladili na základě 6 let praxe a českých interiérů. Žádný americký staging
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/cenik">
                  <Button size="lg" className="bg-[#14A48D] hover:bg-[#11997C] text-white px-8 py-4 rounded-lg text-lg font-semibold">
                    Zobrazit ceník
                  </Button>
                </Link>
                {user && (
                  <Link to="/buy-credits">
                    <Button size="lg" variant="outline" className="border-[#14A48D] text-[#14A48D] hover:bg-[#14A48D] hover:text-white px-8 py-4 rounded-lg text-lg font-semibold">
                      Koupit kredity
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20">
          <div className="container px-4 mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Jak na to? Praktický průvodce krok za krokem</h2>
            {/* Krok 1 */}
            <div className="grid md:grid-cols-2 gap-6 items-center mb-10">
              {/* Levá strana: text */}
              <div>
                <span className="inline-block bg-blue-50 text-blue-900 font-semibold px-4 py-1 rounded-full mb-4 text-sm tracking-widest">KROK 1</span>
                <h3 className="text-4xl font-bold mb-6">Nahrajte obrázky své nemovitosti</h3>
                <p className="text-lg text-gray-700 mb-4">
                  Přetáhněte libovolnou fotogrfii: s nebo bez nábytku pořízený jakýmkoli zařízením.
                </p>
              </div>
              {/* Pravá strana: obrázek */}
              <div className="flex justify-center">
                <img src="/images/a1.png" alt="Ukázka nahrání obrázku" className="rounded-2xl max-w-full w-[567px] h-auto bg-white" />
              </div>
            </div>
            {/* Krok 2 */}
            <div className="grid md:grid-cols-2 gap-6 items-center mb-10">
              {/* Levá strana: obrázek */}
              <div className="flex justify-center">
                <img src="/images/a2.png" alt="Odebrání položky" className="rounded-2xl max-w-full w-[702px] h-auto bg-white" />
              </div>
              {/* Pravá strana: text */}
              <div>
                <span className="inline-block bg-blue-50 text-blue-900 font-semibold px-4 py-1 rounded-full mb-4 text-sm tracking-widest">KROK 2</span>
                <h3 className="text-4xl font-bold mb-6">Odebrání nábytku automaticky</h3>
                <p className="text-lg text-gray-700 mb-4">
                  Náš AI nástroj pro odstranění nábytku (decluttering) automaticky vymaže stávající vybavení z fotografie.
                </p>
              </div>
            </div>
            {/* Krok 3 */}
            <div className="grid md:grid-cols-2 gap-6 items-center mb-10">
              {/* Levá strana: text */}
              <div>
                <span className="inline-block bg-blue-50 text-blue-900 font-semibold px-4 py-1 rounded-full mb-4 text-sm tracking-widest">KROK 3</span>
                <h3 className="text-4xl font-bold mb-6">Vyberte si z předvolby stylů</h3>
                <p className="text-lg text-gray-700 mb-4">
                  Oživte svou kreativní vizi výběrem z navržených stylů interiéru – od moderny po rustikální.
                </p>
              </div>
              {/* Pravá strana: obrázek */}
              <div className="flex justify-center">
                <img src="/images/a3.png" alt="Ukázka výběru stylu" className="rounded-2xl max-w-full w-[567px] h-auto bg-white" />
              </div>
            </div>
            {/* Krok 4 */}
            <div className="grid md:grid-cols-2 gap-6 items-center mb-10">
              {/* Levá strana: obrázky před a po */}
              <div className="flex justify-center">
                <img src="/images/a4.png" alt="Před úpravou" className="rounded-2xl w-[567px] h-auto bg-white" />
              </div>
              {/* Pravá strana: text */}
              <div>
                <span className="inline-block bg-blue-50 text-blue-900 font-semibold px-4 py-1 rounded-full mb-4 text-sm tracking-widest">KROK 4</span>
                <h3 className="text-4xl font-bold mb-6">Získejte realistické výsledky</h3>
                <p className="text-lg text-gray-700 mb-4">
                  Získejte špičkové výsledky, kterých dříve dosáhli pouze odborníci za pouhou 1 minutu na jeden snímek.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Gallery Section */}
        <section className="py-20 bg-gray-50">
          <div className="container px-4 mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12">Před a po</h2>
            <div className="flex flex-col gap-8">
              {BEFORE_AFTER_IMAGES.map((img, idx) => (
                <div key={idx} className="flex flex-col md:flex-row items-center gap-4 bg-gray-50 rounded-lg p-4 shadow">
                  <div className="flex-1 text-center">
                    <div className="mb-2 font-semibold">Před</div>
                    <img src={img.before} alt={`Před - ${img.popis}`} className="rounded max-h-[43rem] w-auto" style={{ maxWidth: '100%', height: 'auto' }} />
                  </div>
                  <div className="flex-1 text-center">
                    <div className="mb-2 font-semibold">Po</div>
                    <img src={img.after} alt={`Po - ${img.popis}`} className="rounded max-h-[43rem] w-auto" style={{ maxWidth: '100%', height: 'auto' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      
      <footer className="bg-gray-900 text-white py-12">
        <div className="container px-4 mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <h2 className="text-xl font-bold">Openstudio</h2>
              <p className="text-gray-400 mt-2">Oživte prázdné prostory s Openstudio</p>
            </div>
            
            <div className="flex flex-col md:flex-row gap-8">
              <div>
                <h3 className="text-lg font-medium mb-2">Služby</h3>
                <ul className="space-y-2">
                  <li><a href="#" className="text-gray-400 hover:text-white">Virtuální zařízení</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white">Interiérový design</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white">Realitní služby</a></li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-medium mb-2">Společnost</h3>
                <ul className="space-y-2">
                  <li><a href="#" className="text-gray-400 hover:text-white">O nás</a></li>
                  <li><a href="/kontakt" className="text-gray-400 hover:text-white">Kontakt</a></li>
                  <li><a href="/ochrana-osobnich-udaju" className="text-gray-400 hover:text-white">Ochrana osobních údajů</a></li>
                  <li><a href="/obchodni-podminky" className="text-gray-400 hover:text-white">Všeobecné obchodní podmínky</a></li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-6 text-center">
            <p className="text-gray-400">© {new Date().getFullYear()} Openstudio. Všechna práva vyhrazena.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
